import { DateRange } from "@ac-expo/utils";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../../axiosInstances";
import {
  eventsExhibitorsQueryKeys,
  eventsQueryKeys,
  organiserExhibitorsQueryKeys,
} from "../../query-keys";
import { EventParticipationRequestsQueryKeys } from "../../query-keys/participation-requests.query-keys";
import { AddOrganiserExhibitorForm } from "../../schemas";

const invalidateEventExhibitorsQueries = (
  queryClient: QueryClient,
  eventId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: organiserExhibitorsQueryKeys.event(eventId),
  });
  queryClient.invalidateQueries({
    queryKey: organiserExhibitorsQueryKeys.eventStructures(eventId),
  });
  queryClient.invalidateQueries({
    queryKey: eventsQueryKeys.get(eventId),
  });
  queryClient.invalidateQueries({
    queryKey: eventsExhibitorsQueryKeys.list(eventId),
  });
};

const invalidateEventExhibitorsWithParticipationQueries = (
  queryClient: QueryClient,
  eventId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: EventParticipationRequestsQueryKeys.event(eventId),
  });
  invalidateEventExhibitorsQueries(queryClient, eventId);
};

const addExhibitors = async (id: string, data: { exhibitors: string[] }) => {
  try {
    const { data: event } = await privateAxios.post(
      `/events/${id}/exhibitors`,
      data,
    );
    return event;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useAddExhibitorsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { exhibitors: string[] }) =>
      addExhibitors(eventId, data),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

const addExhibitorsWithLocations = async (
  id: string,
  data: {
    exhibitors: {
      exhibitorId: string;
      stand?: string;
      hall?: string;
    }[];
  },
) => {
  try {
    const { data: event } = await privateAxios.post(
      `/events/${id}/exhibitors/with-locations`,
      data,
    );
    return event;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useAddExhibitorsWithLocationsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      exhibitors: { exhibitorId: string; stand?: string; hall?: string }[];
    }) => addExhibitorsWithLocations(eventId, data),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

const addEventExhibitorMutation = async (
  eventId: string,
  body: AddOrganiserExhibitorForm,
) => {
  try {
    const { data } = await privateAxios.post(
      `/events/${eventId}/exhibitors/connect`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useAddEventExhibitorMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddOrganiserExhibitorForm) =>
      addEventExhibitorMutation(eventId, data),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

const removeExhibitor = async (id: string, exhibitorId: string) => {
  const { data } = await privateAxios.delete(
    `/events/${id}/exhibitors/${exhibitorId}`,
  );
  return data;
};

export const useRemoveExhibitorMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exhibitorId: string) => removeExhibitor(eventId, exhibitorId),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

const handleRequestParticipation = async (
  eventId: string,
  structureId: string,
  isAccepted: boolean,
  stand?: string,
  hall?: string,
) => {
  const { data } = await privateAxios.patch(
    `/events/${eventId}/exhibitors/requests/${structureId}`,
    {
      isAccepted,
      stand,
      hall,
    },
  );
  return data;
};

export const useHandleRequestParticipationMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      isAccepted,
      stand,
      hall,
    }: {
      structureId: string;
      isAccepted: boolean;
      stand?: string;
      hall?: string;
    }) =>
      handleRequestParticipation(eventId, structureId, isAccepted, stand, hall),
    onSuccess: () =>
      invalidateEventExhibitorsWithParticipationQueries(queryClient, eventId),
  });
};

const validateRequestParticipations = async (
  eventId: string,
  structures: Array<{
    structureId: string;
    stand?: string;
    hall?: string;
  }>,
) => {
  const { data } = await privateAxios.patch(
    `/events/${eventId}/exhibitors/requests`,
    {
      structures,
    },
  );
  return data;
};

export const useValidateRequestParticipationsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      structures: Array<{
        structureId: string;
        stand?: string;
        hall?: string;
      }>,
    ) => validateRequestParticipations(eventId, structures),
    onSuccess: () =>
      invalidateEventExhibitorsWithParticipationQueries(queryClient, eventId),
  });
};

const getExhibitorManyEndpoint = (eventId?: string) =>
  eventId ? `/events/${eventId}/exhibitors/many` : "/organiser/exhibitors/many";

type CreateExhibitorFromCsvData = {
  address?: string | null;
  businessName: string;
  businessSector?: string | null;
  businessEmail: string;
  phoneNumber?: string | null;
  city?: string | null;
  zipCode?: string | null;
  websiteUrl?: string | null;
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  twitterUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
};

const createExhibitorsFromCsv = async (
  data: CreateExhibitorFromCsvData[],
  eventId?: string,
) => {
  try {
    const { data: response } = await privateAxios.post(
      getExhibitorManyEndpoint(eventId),
      { exhibitors: data },
    );
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useCreateExhibitorsFromCsv = (eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExhibitorFromCsvData[]) =>
      createExhibitorsFromCsv(data, eventId),
    onSuccess: () => {
      if (eventId) {
        invalidateEventExhibitorsQueries(queryClient, eventId);
      } else {
        queryClient.invalidateQueries({
          queryKey: organiserExhibitorsQueryKeys.me(),
        });
      }
    },
  });
};

const deleteExhibitorsFromCsv = async (
  exhibitorIds: string[],
  eventId?: string,
) => {
  try {
    const { data: response } = await privateAxios.delete(
      getExhibitorManyEndpoint(eventId),
      {
        data: {
          exhibitorIds,
        },
      },
    );
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteExhibitorsFromCsv = (eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exhibitorIds: string[]) =>
      deleteExhibitorsFromCsv(exhibitorIds, eventId),
    onSuccess: () => {
      if (eventId) {
        invalidateEventExhibitorsQueries(queryClient, eventId);
      } else {
        queryClient.invalidateQueries({
          queryKey: organiserExhibitorsQueryKeys.me(),
        });
      }
    },
  });
};

export type PatchExhibitorFromCsvData = {
  existingId: string;
  data: CreateExhibitorFromCsvData;
};

const patchExhibitorsFromCsv = async (
  data: PatchExhibitorFromCsvData[],
  eventId?: string,
) => {
  try {
    const { data: response } = await privateAxios.patch(
      getExhibitorManyEndpoint(eventId),
      {
        exhibitors: data,
      },
    );
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const usePatchExhibitorsFromCsv = (eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatchExhibitorFromCsvData[]) =>
      patchExhibitorsFromCsv(data, eventId),
    onSuccess: () => {
      if (eventId) {
        invalidateEventExhibitorsQueries(queryClient, eventId);
      } else {
        queryClient.invalidateQueries({
          queryKey: organiserExhibitorsQueryKeys.me(),
        });
      }
    },
  });
};

type UpdateEventExhibitorsSettingBody = {
  exhibitorIds: string[];
};

const updateEventExhibitorsSettingPacks = async (
  eventId: string,
  settingId: string,
  body: UpdateEventExhibitorsSettingBody,
) => {
  try {
    const { data } = await privateAxios.put(
      `/events/${eventId}/exhibitors/settings/packs/${settingId}`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateEventExhibitorsSettingPacksMutation = (
  eventId: string,
  settingId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body }: { body: UpdateEventExhibitorsSettingBody }) =>
      updateEventExhibitorsSettingPacks(eventId, settingId, body),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

const removeEventExhibitorFromSettingsPacksExhibitors = async (
  eventId: string,
  settingId: string,
  body: UpdateEventExhibitorsSettingBody,
) => {
  const { data } = await privateAxios.delete(
    `/events/${eventId}/exhibitors/settings/packs/${settingId}/exhibitors`,
    {
      data: body,
    },
  );
  return data;
};

export const useRemoveEventExhibitorsSettingsPacksExhibitorsMutation = (
  eventId: string,
  settingId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateEventExhibitorsSettingBody) =>
      removeEventExhibitorFromSettingsPacksExhibitors(eventId, settingId, body),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

type UpdateExhibitorPackBody = {
  exhibitorProfilesPack: string | null;
};

const updateExhibitorPack = async (
  eventId: string,
  exhibitorId: string,
  body: UpdateExhibitorPackBody,
) => {
  try {
    const { data } = await privateAxios.patch(
      `/events/${eventId}/exhibitors/${exhibitorId}/pack`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateExhibitorPackMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      exhibitorId,
      body,
    }: {
      exhibitorId: string;
      body: UpdateExhibitorPackBody;
    }) => updateExhibitorPack(eventId, exhibitorId, body),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};

type UpdateExhibitorPromotionDateRangesBody = {
  promotionDateRanges: DateRange[];
};

const updateExhibitorPromotionDateRanges = async (
  eventId: string,
  exhibitorId: string,
  body: UpdateExhibitorPromotionDateRangesBody,
) => {
  try {
    const { data } = await privateAxios.patch(
      `/events/${eventId}/exhibitors/${exhibitorId}/promotion-date-ranges`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateExhibitorPromotionDateRangesMutation = (
  eventId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      exhibitorId,
      body,
    }: {
      exhibitorId: string;
      body: UpdateExhibitorPromotionDateRangesBody;
    }) => updateExhibitorPromotionDateRanges(eventId, exhibitorId, body),
    onSuccess: () => invalidateEventExhibitorsQueries(queryClient, eventId),
  });
};
